import { DonutChart } from "@mantine/charts";
import { Anchor, Card, ColorSwatch, Group, Skeleton, Stack, Text } from "@mantine/core";
import { IconChartDonut } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { DashboardData } from "../../../apis/myDashboard";
import { EmptyState } from "../EmptyState";
import { SectionHeader } from "../SectionHeader";

interface Props {
  data?: DashboardData;
  loading: boolean;
}

const SCOPE_LABEL: Record<"DEFAULT" | "NAMED", string> = {
  DEFAULT: "Үндсэн (DEFAULT)",
  NAMED  : "Тусгай (NAMED)"
};
const SCOPE_COLOR: Record<"DEFAULT" | "NAMED", string> = {
  DEFAULT: "teal.6",
  NAMED  : "grape.6"
};

// ROW 2 — how many customers sit on the DEFAULT vs a NAMED fee policy (UI-SPEC §4.2).
export function PolicyBreakdownDonut({ data, loading }: Props) {
  const navigate = useNavigate();
  const breakdown = data?.policyBreakdown ?? [];
  const total = breakdown.reduce((sum, b) => sum + b.customers, 0);
  const named = breakdown.find((b) => b.scope === "NAMED")?.customers ?? 0;
  const chartData = breakdown
    .filter((b) => b.customers > 0)
    .map((b) => ({ name: SCOPE_LABEL[b.scope], value: b.customers, color: SCOPE_COLOR[b.scope] }));

  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm" h="100%">
        <SectionHeader title="Бодлогоор задаргаа" eyebrow="Харилцагчид" />
        {loading ? (
          <Skeleton height={240} radius="sm" />
        ) : total === 0 ? (
          <EmptyState icon={<IconChartDonut size={26} />} message="Идэвхтэй харилцагч алга байна." />
        ) : (
          <Stack gap="md" align="center" pt="xs">
            <DonutChart
              data={chartData}
              size={170}
              thickness={22}
              withTooltip
              chartLabel={`${total} харилцагч`}
            />
            <Stack gap={8} w="100%">
              {breakdown.map((b) => (
                <Group key={b.scope} justify="space-between" wrap="nowrap">
                  <Group gap={8} wrap="nowrap">
                    <ColorSwatch color={`var(--mantine-color-${SCOPE_COLOR[b.scope].replace(".", "-")})`} size={12} withShadow={false} />
                    <Text size="sm">{SCOPE_LABEL[b.scope]}</Text>
                  </Group>
                  <Text size="sm" fw={600}>{b.pct}% · {b.customers}</Text>
                </Group>
              ))}
            </Stack>
            {named === 0 && (
              <Anchor component="button" type="button" size="xs" onClick={() => navigate("/my-billing/fee-policy")}>
                Тусгай үнэ (NAMED) бодлого үүсгэх
              </Anchor>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
